import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, X } from 'lucide-react';
import styles from './CardDetailCreateLabelModal.module.scss';

export interface CardDetailCreateLabelModalProps {
  show: boolean;
  newLabelName: string;
  newLabelColor: string;
  availableLabelColors: string[];
  onNewLabelNameChange: (v: string) => void;
  onNewLabelColorChange: (v: string) => void;
  onRemoveLabelColor: () => void;
  onCreateLabel: () => void | Promise<void>;
  onDeleteLabel?: () => void | Promise<void>;
  isEditMode?: boolean;
  onClose: () => void;
  defaultLabelColor: string;
}

export default function CardDetailCreateLabelModal({
  show,
  newLabelName,
  newLabelColor,
  availableLabelColors,
  onNewLabelNameChange,
  onNewLabelColorChange,
  onRemoveLabelColor,
  onCreateLabel,
  onDeleteLabel,
  isEditMode = false,
  onClose,
  defaultLabelColor,
}: CardDetailCreateLabelModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleCreate = useCallback(async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    // Close immediately so the user doesn't see the dialog twice / flicker.
    onClose();
    try {
      await onCreateLabel();
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  }, [onClose, onCreateLabel]);

  if (!show) return null;

  return createPortal(
    <div
      className={styles.createLabelModalLayer}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-label-title"
    >
      <div
        className={styles.createLabelModalBackdrop}
        onClick={onClose}
        aria-hidden
      />
      <div className={styles.createLabelModalDialog}>
        <div className={styles.createLabelModalHeader}>
          <button
            type="button"
            className={styles.createLabelModalBackBtn}
            onClick={onClose}
            aria-label="Back to labels"
          >
            <ChevronLeft size={20} />
          </button>
          <h2 id="create-label-title" className={styles.createLabelModalTitle}>
            {isEditMode ? 'Edit label' : 'Create label'}
          </h2>
          <button
            type="button"
            className={styles.createLabelModalCloseBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div
          className={styles.createLabelPreviewBar}
          style={{
            background: newLabelColor ?? defaultLabelColor,
          }}
        />
        <div className={styles.createLabelModalBody}>
          <label className={styles.createLabelFieldLabel}>Title</label>
          <input
            type="text"
            className={styles.createLabelTitleInput}
            value={newLabelName}
            onChange={(e) => onNewLabelNameChange(e.target.value)}
            placeholder="Label name"
            aria-label="Label title"
          />
          <label className={styles.createLabelFieldLabel}>Select a color</label>
          <div className={styles.createLabelColorGrid}>
            {availableLabelColors.map((color: string) => (
              <button
                key={color}
                type="button"
                className={styles.createLabelColorOption}
                style={{ background: color }}
                onClick={() => onNewLabelColorChange(color)}
                aria-label={`Color ${color}`}
                title={color}
              >
                {newLabelColor === color && (
                  <span className={styles.createLabelColorCheck}>✓</span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.removeColorBtn}
            onClick={onRemoveLabelColor}
          >
            <X size={14} />
            Remove color
          </button>
          {isEditMode && onDeleteLabel && (
            <button
              type="button"
              className={styles.deleteLabelBtn}
              onClick={() => void onDeleteLabel()}
              disabled={isSubmitting}
            >
              Delete label
            </button>
          )}
          <button
            type="button"
            className={styles.createLabelSubmitBtn}
            onClick={() => void handleCreate()}
            disabled={!newLabelName.trim() || isSubmitting}
          >
            {isEditMode ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
